import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Heart, Activity, Zap, TrendingUp, AlertTriangle, ChevronRight, Sparkles,
} from 'lucide-react';
import { Progress } from '../components/ui/progress';
import DashboardLayout from '../components/DashboardLayout';
import PageHeader from '../components/PageHeader';
import MuscleRecoveryMap from '../components/MuscleRecoveryMap';
import RecoveryWhyPanel from '../components/RecoveryWhyPanel';
import PillarTrendChart from '../components/PillarTrendChart';
import SoundShelf from '../components/mental/SoundShelf';
import CheckinNudge from '../components/CheckinNudge';
import ScoreBreakdownPopover from '../components/ScoreBreakdownPopover';
import api from '../services/api';

/**
 * Recovery page (iter67 redesign).
 *
 * Anchored on the user's asks:
 *  - Body map = chat-style (full green when all muscles fresh). Single
 *    source via <MuscleRecoveryMap />.
 *  - WHY panel = synthesized cross-pillar drivers (sleep + nutrition +
 *    workout fatigue + stress + body signals). Powered by
 *    /api/wellness/iq → recovery_insight. Nikki uses the same data.
 *  - Score transparency = (i) tooltip in header shows the full breakdown.
 *  - Training-ready chips hover to reveal each muscle's recovery_pct.
 *  - REMOVED: Log Vitals card (sleep already lives in the daily check-in).
 *  - REMOVED: stale 3-step "How Recovery Works" panel — replaced by the
 *    same transparent (i) breakdown the dashboard rings use.
 */

const formatMuscle = (m) => m.split('_').map(p => p[0].toUpperCase() + p.slice(1)).join(' ');
const getScoreColor = (s) => s >= 85 ? '#a3e635' : s >= 65 ? '#CCFF00' : s >= 45 ? '#facc15' : s >= 25 ? '#fb923c' : '#f87171';

const MuscleChip = ({ muscle, pct, color, testId }) => (
  <span
    className="group relative px-3 py-1.5 rounded-sm text-xs font-secondary uppercase cursor-default border"
    style={{ color, backgroundColor: `${color}1A`, borderColor: `${color}33` }}
    data-testid={testId}
  >
    {formatMuscle(muscle)}
    <span
      className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 rounded-sm bg-zinc-950/95 border border-zinc-700 text-[10px] uppercase tracking-wider whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-20"
      style={{ color }}
    >
      {pct}% recovered
    </span>
  </span>
);

const Recovery = ({ embedded = false }) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Deep-link from the District: /recovery?room=sounds|clinic scrolls to (and
  // highlights) that specific "building" section, so tapping a building opens
  // ITS room, not the whole page.
  useEffect(() => {
    const room = new URLSearchParams(location.search).get('room');
    if (!room) return;
    let tries = 0;
    const iv = setInterval(() => {
      const el = document.getElementById(`room-${room}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el.style.transition = 'box-shadow 0.35s ease';
        el.style.boxShadow = '0 0 0 3px rgba(95,198,232,0.7)';
        el.style.borderRadius = '18px';
        setTimeout(() => { el.style.boxShadow = ''; }, 1600);
        clearInterval(iv);
      } else if (++tries > 24) { clearInterval(iv); }
    }, 150);
    return () => clearInterval(iv);
  }, [location.search]);
  const [bodyMap, setBodyMap] = useState(null);
  const [injuryRisk, setInjuryRisk] = useState(null);
  const [iq, setIq] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [bodyRes, riskRes, iqRes] = await Promise.allSettled([
        api.get('/recovery/body-map'),
        api.getInjuryRisk(),
        api.getWellnessIQ(),
      ]);
      if (bodyRes.status === 'fulfilled') setBodyMap(bodyRes.value.data);
      if (riskRes.status === 'fulfilled') setInjuryRisk(riskRes.value.data);
      if (iqRes.status === 'fulfilled') setIq(iqRes.value.data);
    } catch (e) {
      // soft-fail — page still renders with empty states
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
    const h = () => fetchData();
    window.addEventListener('wellness:refresh', h);
    return () => window.removeEventListener('wellness:refresh', h);
  }, [fetchData]);

  const muscles = bodyMap?.muscle_groups || {};
  // "All fresh" if (a) no muscle data OR (b) every muscle is at >=100% (untrained default
  // from /recovery/body-map). Treated identically by the visual + chip lists.
  const muscleEntries = Object.entries(muscles);
  const allUntrained = muscleEntries.length > 0 && muscleEntries.every(([, v]) => (v?.recovery_pct ?? 100) >= 100);
  const hasMuscleData = muscleEntries.length > 0 && !allUntrained;
  const overall = hasMuscleData
    ? Math.round(Object.values(muscles).reduce((a, m) => a + m.recovery_pct, 0) / Object.values(muscles).length)
    : null;
  const recoveryIQ = iq?.sub_scores?.recovery;
  const recoveryInsight = iq?.recovery_insight;
  const recoveryBreakdown = iq?.score_breakdown?.recovery;

  // All 11 muscles when no data, otherwise classify by 85% threshold.
  const ALL_MUSCLES = ['shoulders','chest','upper_back','biceps','triceps','core','lower_back','quads','hamstrings','glutes','calves'];
  const muscleList = hasMuscleData
    ? Object.entries(muscles)
    : ALL_MUSCLES.map(m => [m, { recovery_pct: 100, color: '#a3e635' }]);
  const fatigued  = muscleList.filter(([, d]) => d.recovery_pct < 85).sort((a, b) => a[1].recovery_pct - b[1].recovery_pct);
  const recovered = muscleList.filter(([, d]) => d.recovery_pct >= 85);

  if (loading) {
    const skeleton = (
      <div className={embedded ? '' : 'max-w-6xl mx-auto px-4 py-8'}>
        <div className="h-8 w-48 bg-zinc-800 rounded animate-pulse mb-4" />
        <div className="h-64 bg-zinc-800 rounded animate-pulse" />
      </div>
    );
    return embedded ? skeleton : <DashboardLayout>{skeleton}</DashboardLayout>;
  }

  const body = (
      <div className={embedded ? 'max-w-2xl mx-auto px-5 py-8 space-y-6 living-skin' : 'max-w-6xl mx-auto px-4 py-8 space-y-6'} style={embedded ? { '--skin-rgb': '34,211,238' } : undefined}>

        {/* Header — Recovery score + transparency tooltip */}
        {!embedded && (
        <div className="flex items-start justify-between gap-4">
          <PageHeader
            tag="Recovery Intelligence"
            tagColor="#22d3ee"
            icon={Heart}
            title="How fresh are you today?"
            subtitle="Your body's recovery, fed by sleep, food, stress and recent training — connected, not isolated."
            testIdPrefix="recovery"
          />
          {recoveryIQ != null && (
            <div className="flex items-center gap-2 shrink-0">
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-secondary">Recovery IQ</p>
                <p className="font-secondary font-bold text-3xl" style={{ color: getScoreColor(recoveryIQ) }} data-testid="recovery-iq-score">{recoveryIQ}</p>
              </div>
              <ScoreBreakdownPopover
                breakdown={recoveryBreakdown}
                label="Recovery formula"
                finalScore={recoveryIQ}
                color="#22d3ee"
                testId="recovery-breakdown"
              />
            </div>
          )}
        </div>
        )}

        {/* Entry point → the Living "Recovery District". Classic page only; the
            Living world renders the shared District button from World.js hub. */}
        {!embedded && (
        <button
          onClick={() => navigate('/district/recovery')}
          data-testid="enter-recovery-district-btn"
          className="w-full group relative overflow-hidden rounded-2xl border border-cyan-400/30 bg-gradient-to-r from-cyan-500/15 via-sky-500/10 to-transparent px-5 py-4 flex items-center justify-between transition-all hover:border-cyan-400/60 hover:from-cyan-500/25"
        >
          <span className="flex items-center gap-3 text-left">
            <span className="w-11 h-11 rounded-2xl grid place-items-center bg-cyan-500/20 border border-cyan-400/40 shrink-0">
              <Sparkles className="w-5 h-5 text-cyan-300" />
            </span>
            <span className="leading-tight">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-cyan-300/90">New · Living Layer</span>
              <span className="block font-secondary font-bold text-white text-base">Enter your Recovery District</span>
              <span className="block text-xs text-zinc-400">Earn coins from healthy actions and build your world.</span>
            </span>
          </span>
          <ChevronRight className="w-5 h-5 text-cyan-300 shrink-0 transition-transform group-hover:translate-x-1" />
        </button>
        )}



        {/* Cross-pillar WHY panel — drives the page's coaching. Hidden in the
            embedded world: WorldView already shows Nikki's "why this world looks
            like this" read above, so this would just repeat it. */}
        {!embedded && (
        <RecoveryWhyPanel
          insight={recoveryInsight}
          score={recoveryIQ}
          onActionClick={() => navigate('/chat')}
        />
        )}

        <PillarTrendChart pillar="recovery" color="#22d3ee" label="Recovery" />

        {/* Check-in lives on the Dashboard now — this is just a quick link. */}
        <CheckinNudge color="#22d3ee" />

        <div id="room-sounds" style={{ scrollMarginTop: '90px' }}>
          <SoundShelf pillar="recovery" />
        </div>

        {/* Body Map + Muscle lists */}
        <div id="room-clinic" style={{ scrollMarginTop: '90px' }} className="grid lg:grid-cols-2 gap-6">

          {/* Body Map — chat-style: full green when no fatigue data */}
          <div className="hpme-card p-6">
            <div className="hpme-card-corner" />
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-secondary font-bold text-lg">MUSCLE RECOVERY MAP</h3>
              {hasMuscleData ? (
                <div className="flex items-center gap-2">
                  <div className="w-32 sm:w-40 h-2 rounded-full" style={{ background: 'linear-gradient(to right, #ef4444, #fb923c, #fde047, #a3e635)' }} />
                  <span className="text-[10px] text-zinc-500">0 → 100%</span>
                </div>
              ) : (
                <span className="text-[10px] uppercase tracking-wider" style={{ color: '#a3e635' }} data-testid="muscle-map-all-fresh">All fresh</span>
              )}
            </div>
            <div className="flex items-center justify-center">
              <MuscleRecoveryMap muscleData={muscles} size="md" interactive={true} />
            </div>
            <p className="text-center text-[10px] uppercase tracking-wider text-zinc-400 mt-1.5" data-testid="recovery-map-hint">
              Tap or hover a muscle to see its recovery %
            </p>
            {overall != null && (
              <div className="mt-4 text-center">
                <p className="text-zinc-500 text-[10px] uppercase tracking-wider">Average muscle recovery</p>
                <p className="font-secondary font-bold text-3xl mt-1" style={{ color: getScoreColor(overall) }} data-testid="overall-recovery">{overall}%</p>
              </div>
            )}
          </div>

          {/* Muscle Details */}
          <div className="space-y-4">

            {/* Recovering muscles */}
            {fatigued.length > 0 && (
              <div className="hpme-card p-5">
                <div className="hpme-card-corner" />
                <h3 className="font-secondary font-bold text-lg mb-1 flex items-center gap-2">
                  <Zap className="w-4 h-4 text-orange-400" />
                  RECOVERING ({fatigued.length})
                </h3>
                <p className="text-zinc-500 text-xs mb-3">Below 85% — go light or rest these</p>
                <div className="space-y-3">
                  {fatigued.map(([muscle, data]) => (
                    <div key={muscle} data-testid={`recovery-${muscle}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-zinc-300">{formatMuscle(muscle)}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-secondary font-bold text-sm" style={{ color: data.color || getScoreColor(data.recovery_pct) }}>
                            {data.recovery_pct}%
                          </span>
                          {data.hours_since_trained && (
                            <span className="text-xs text-zinc-500">
                              {data.hours_since_trained < 24
                                ? `${Math.round(data.hours_since_trained)}h ago`
                                : `${Math.round(data.hours_since_trained / 24)}d ago`}
                            </span>
                          )}
                        </div>
                      </div>
                      <Progress value={data.recovery_pct} className="h-2 bg-zinc-800" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Training-ready chips with hover tooltips */}
            <div className="hpme-card p-5">
              <div className="hpme-card-corner" />
              <h3 className="font-secondary font-bold text-lg mb-1 flex items-center gap-2">
                <Heart className="w-4 h-4 text-green-400" />
                TRAINING READY ({recovered.length})
              </h3>
              <p className="text-zinc-500 text-xs mb-3">≥ 85% — fresh enough to train hard</p>
              {recovered.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {recovered.map(([muscle, data]) => (
                    <MuscleChip
                      key={muscle}
                      muscle={muscle}
                      pct={data.recovery_pct}
                      color={data.color || '#a3e635'}
                      testId={`ready-chip-${muscle}`}
                    />
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500 text-sm">All muscles are still recovering</p>
              )}
            </div>

            {/* Workouts this week */}
            <div className="hpme-card p-5">
              <div className="hpme-card-corner" />
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-zinc-500 text-xs uppercase">Workouts this week</p>
                  <p className="font-secondary font-bold text-2xl text-primary" data-testid="workouts-this-week">{bodyMap?.workouts_this_week || 0}</p>
                </div>
              </div>
            </div>

            {/* Injury risk — kept compact */}
            {injuryRisk && (
              <div className="hpme-card p-5" data-testid="injury-risk-card">
                <div className="hpme-card-corner" />
                <h3 className="font-secondary font-bold text-sm mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-400" />
                  INJURY RISK · {injuryRisk.level?.toUpperCase() || 'LOW'}
                </h3>
                {Array.isArray(injuryRisk.factors) && injuryRisk.factors.length > 0 && (
                  <div className="space-y-1.5">
                    {injuryRisk.factors.slice(0, 3).map((f, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-zinc-400">
                        <ChevronRight className="w-3 h-3 mt-0.5 text-zinc-600 shrink-0" />
                        <span>{typeof f === 'string' ? f : (f.description || f.factor || f.name)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
  );
  return embedded ? body : <DashboardLayout>{body}</DashboardLayout>;
};

export default Recovery;
